import { CloudUpload, Download, Upload, X } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  BrowseButton,
  ClearButton,
  Container,
  DownloadLink,
  HelperList,
  HelperListItem,
  HiddenInput,
  SelectedFile,
  UploadBox,
  UploadContent,
  UploadDescription,
  UploadIcon,
  UploadTitle,
} from './ExcelUploader.styles'
import { Button, Flex } from '@/components'

interface ExcelUploadProps {
  file?: File | null
  onFileSelect: (file: File | null) => void
  onImport: () => void
  isImporting?: boolean
  acceptedFormats?: string[]
}

export const ExcelUpload: React.FC<ExcelUploadProps> = ({
  file: externalFile,
  onFileSelect,
  onImport,
  isImporting = false,
  acceptedFormats = ['.xls', '.xlsx'],
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  useEffect(() => {
    if (externalFile === null) {
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [externalFile])

  function selectFile(file: File) {
    setSelectedFile(file)
    onFileSelect(file)
  }

  function clearFile(event: React.MouseEvent) {
    event.stopPropagation()
    setSelectedFile(null)
    onFileSelect(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) selectFile(file)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files[0]
    if (file && acceptedFormats.some((format) => file.name.endsWith(format))) {
      selectFile(file)
    }
  }

  return (
    <Container>
      <UploadBox onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} $isDragging={isDragging}>
        <UploadIcon>
          <CloudUpload />
        </UploadIcon>
        <UploadContent>
          <UploadTitle>Importação rápida via Excel</UploadTitle>
          <UploadDescription>
            Arraste e solte a sua planilha ({acceptedFormats.join(', ')}) aqui ou clique para selecionar.{' '}
            <DownloadLink href="/templates/template-publicadores.xlsx" download="template-publicadores.xlsx">
              <Download size={14} />
              Baixar template
            </DownloadLink>
          </UploadDescription>

          <HelperList>
            <HelperListItem>
              <strong>nome</strong> — nome da pessoa/página (repita em outra linha para outra rede)
            </HelperListItem>
            <HelperListItem>
              <strong>rede</strong> — Twitter, Instagram, Facebook ou YouTube
            </HelperListItem>
            <HelperListItem>
              <strong>link_perfil</strong> — link completo do perfil, com https://. Para mais de um perfil na mesma
              rede, separe por ponto e vírgula (ex: "https://instagram.com/perfil;https://instagram.com/perfil2"). Copie
              o link exatamente como aparece na rede social.
            </HelperListItem>
            <HelperListItem>
              <strong>grupos</strong> — um ou mais grupos separados por vírgula (ex: "Políticos,Influenciadores")
            </HelperListItem>
            <HelperListItem>
              Vários links na mesma célula só funcionam em <strong>.xlsx</strong> — no .csv o ponto e vírgula é lido
              como separador de coluna.
            </HelperListItem>
          </HelperList>

          {selectedFile && (
            <SelectedFile>
              <Flex $type="row" $align="center" $gap={8}>
                Arquivo selecionado: <strong>{selectedFile.name}</strong>
                <ClearButton type="button" onClick={clearFile} title="Remover arquivo">
                  <X size={14} />
                </ClearButton>
              </Flex>
            </SelectedFile>
          )}
        </UploadContent>
        <Flex $type="column" $align="center" $gap={8}>
          <BrowseButton onClick={() => fileInputRef.current?.click()}>Procurar Ficheiro</BrowseButton>
          <HiddenInput ref={fileInputRef} type="file" accept={acceptedFormats.join(',')} onChange={handleFileChange} />
        </Flex>
      </UploadBox>

      {selectedFile && (
        <Button.Root
          type="button"
          $variant="primary"
          onClick={onImport}
          disabled={isImporting}
          style={{ width: '100%', marginTop: '12px' }}>
          <Button.Icon value={Upload} size={16} />
          <Button.Text value={isImporting ? 'Importando...' : 'Importar Planilha'} />
        </Button.Root>
      )}
    </Container>
  )
}
